import React, { useEffect, useMemo, useRef } from "react";
import { ConnectedProps, connect } from "react-redux";
import { MapContainer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ReducerState } from "../../types";
import { getOrganization } from "../../actions";
import L, { LatLngTuple } from "leaflet";
import { useWebServer } from "../../providers";
import { Spin } from "antd";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import "./index.css";
import MarkerManager from "./MarkerManager";
import MapExpand from "./MapExpand";
import MapDownloadControl from "./MapDownload";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { MarkerData } from "../../types/components/map";
import { TrapSite } from "../../types/api/models/trapSite";
import {
  useMaintenanceAlertsCounts,
  useDetectionAlertsCounts,
} from "../../hooks/useAppAlerts";
import SatelliteLayer from "./SatelliteLayer";

const mapStateToProps = (state: ReducerState) => {
  const map_center = state.devices?.selected?.gps ||
    state.organizations?.map_center || { lat: 32.7361613, lon: -98.2829359 };
  return {
    uuid: state.organizations?.uuid || null,
    map_center: [map_center?.lat, map_center?.lon] as LatLngTuple,
  };
};

const connector = connect(mapStateToProps, {
  getOrganization,
});

type Props = ConnectedProps<typeof connector> & {
  /** When provided, only the marker for this trapsite is shown (mesh/trapsite routes). */
  trapSite?: Partial<TrapSite>;
  deviceId?: string;
};

const MapViewController: React.FC<{
  zoom: number;
  markers: LatLngTuple[];
}> = ({ zoom, markers }) => {
  const map = useMap();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    if (markers.length === 0) return;
    if (markers.length > 1) {
      map.fitBounds(L.latLngBounds(markers), { padding: [10, 10] });
    } else {
      map.setView(markers[0], zoom);
    }
    initialized.current = true;
  }, [map, markers, zoom]);

  return null;
};

const MapComponent: React.FC<Props> = ({
  map_center,
  uuid,
  getOrganization,
  trapSite,
  deviceId,
}) => {
  const { sendRequest, isLoading } = useWebServer();

  useEffect(() => {
    if (!isLoading && !uuid) {
      getOrganization(sendRequest);
    }
  }, [getOrganization, sendRequest, isLoading, uuid]);

  const { counts: maintenanceCounts, isLoading: isMaintenanceLoading } =
    useMaintenanceAlertsCounts();
  const { counts: detectionCounts, isLoading: isDetectionLoading } =
    useDetectionAlertsCounts();

  const { data: trapSitesData, isLoading: isTrapSiteLoading } =
    useListTrapSitesQuery();
  const { data: devices } = useListDevicesQuery();

  const isDataLoading =
    isTrapSiteLoading || isMaintenanceLoading || isDetectionLoading;

  const markersData = useMemo((): MarkerData[] => {
    if (isDataLoading || !devices?.items || !trapSitesData?.items) return [];

    const deviceAgg = devices.items.map((device) => {
      const { uuid, display_name, trap_site_id, gps } = device;

      return {
        uuid: uuid || "",
        deviceName: display_name || "N/A",
        maintainCount: maintenanceCounts[device?.uuid || ""] || 0,
        detectionCount: detectionCounts[device?.uuid || ""] || 0,
        trapSite: trap_site_id || "",
        gps,
      };
    });

    return trapSitesData.items.map((trapSite) => {
      const devicesInTrapSite = deviceAgg.filter(
        (device) => device.trapSite === trapSite._id,
      );
      const detectionCount = devicesInTrapSite.reduce(
        (sum, d) => sum + (d.detectionCount || 0),
        0,
      );
      const maintainCount = devicesInTrapSite.reduce(
        (sum, d) => sum + (d.maintainCount || 0),
        0,
      );
      const trapCount = devicesInTrapSite.length;

      return {
        id: trapSite._id,
        displayName: trapSite.display_name,
        position: [
          trapSite.gps?.lat || 0,
          trapSite.gps?.lon || 0,
        ] as LatLngTuple,
        warning: maintainCount > 0 || detectionCount > 0,
        detectionsCount: detectionCount,
        maintainCount: maintainCount,
        trapCount: trapCount,
        devicesInTrapSite: devicesInTrapSite,
        popupOpen: true,
      };
    });
  }, [
    devices,
    trapSitesData,
    maintenanceCounts,
    detectionCounts,
    isDataLoading,
  ]);

  // If deviceId is provided, use it as the map center; (specimen/mesh page)
  // otherwise fall back to trapSite GPS (location detail page) or Redux map_center(home page).
  const effectiveCenter = useMemo((): LatLngTuple => {
    if (deviceId) {
      const device = devices?.items?.find((d) => d.uuid === deviceId);
      if (device?.gps?.lat && device?.gps?.lon) {
        return [device.gps.lat, device.gps.lon];
      }
    }

    if (trapSite?.gps?.lat && trapSite?.gps?.lon) {
      return [trapSite.gps.lat, trapSite.gps.lon];
    }
    return map_center;
  }, [trapSite, map_center, deviceId, devices]);

  // When a specific trapSite is provided (mesh/specimen routes), show device-level markers.
  const visibleMarkers = useMemo((): MarkerData[] => {
    if (!trapSite) return markersData;

    const match = markersData.find((m) => m.id === trapSite._id);
    if (!match?.devicesInTrapSite?.length) {
      return match ? [match] : [];
    }

    // If a specific device is requested, narrow to just that device.
    const relevantDevices = deviceId
      ? match.devicesInTrapSite.filter((d) => d.uuid === deviceId)
      : match.devicesInTrapSite;

    // Split devices: those WITH GPS get individual markers,
    // those WITHOUT GPS are aggregated into one trapSite-level marker.
    const withGps = relevantDevices.filter((d) => d.gps?.lat && d.gps?.lon);
    const withoutGps = relevantDevices.filter(
      (d) => !d.gps?.lat || !d.gps?.lon,
    );

    const deviceMarkers: MarkerData[] = withGps.map((device) => ({
      displayName: device.deviceName,
      position: [device.gps!.lat, device.gps!.lon] as LatLngTuple,
      warning:
        (device.maintainCount || 0) > 0 || (device.detectionCount || 0) > 0,
      detectionsCount: device.detectionCount || 0,
      maintainCount: device.maintainCount || 0,
      trapCount: 1,
      markerType: "device" as const,
      gps: device.gps,
      popupOpen: !deviceId,
    }));

    // Aggregate GPS-less devices into one marker at the trapSite position
    if (withoutGps.length > 0) {
      deviceMarkers.push({
        displayName: match.displayName,
        position: match.position,
        warning: withoutGps.some(
          (d) => (d.maintainCount || 0) > 0 || (d.detectionCount || 0) > 0,
        ),
        detectionsCount: withoutGps.reduce(
          (s, d) => s + (d.detectionCount || 0),
          0,
        ),
        maintainCount: withoutGps.reduce(
          (s, d) => s + (d.maintainCount || 0),
          0,
        ),
        trapCount: withoutGps.length,
        popupOpen: !deviceId,
      });
    }

    return deviceMarkers;
  }, [markersData, trapSite, deviceId]);

  const zoom = deviceId ? 20 : trapSite ? 18 : 13;

  return (
    <div className="map-container-big">
      {uuid && !isLoading ? (
        <MapContainer
          worldCopyJump={true}
          center={effectiveCenter}
          minZoom={3}
          maxZoom={20}
          zoom={zoom}
          scrollWheelZoom={true}
          style={{
            width: "100%",
            height: "100%",
          }}
          zoomControl={false}
        >
          <MapViewController
            zoom={zoom}
            markers={visibleMarkers.map((c) => c.position)}
          />
          <SatelliteLayer />
          <MarkerManager
            markersData={visibleMarkers}
            isLoading={isDataLoading}
          />
          <MapExpand />
          <MapDownloadControl />
        </MapContainer>
      ) : (
        <div className="place-holder-map">
          <Spin />
        </div>
      )}
    </div>
  );
};

export default connector(MapComponent);
